import { format } from 'date-fns';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { TimeLeft } from 'shared/components/ui/time-left';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import * as Styled from './meta-deadline-deadline.style';

const datePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.Friday,
];

export type MetaDeadlineDeadlineProps = {
  value?: Timestamp;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onChange: (date?: Timestamp) => void;
};

const OPEN_MENU_KEYS = [['KeyD']];

export const MetaDeadlineDeadline: React.FC<MetaDeadlineDeadlineProps> = ({
  value,
  onChange,
  weekStartsOn,
  dateFormat,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Must hold Shift for this shortcut
      if (!e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useKeysDown(OPEN_MENU_KEYS, onShortcut, {
    ignoreInputFields: true,
    capture: true,
    otherKeysInCombinationAllowed: true,
  });

  const { due } = useMemo(() => {
    if (!value) {
      return { due: undefined, overdue: false };
    }

    return { due: format(value, 'd MMM'), overdue: false };
  }, [value]);

  const _onChange = (date?: Timestamp) => {
    onChange(date);
    closeMenu();
  };

  const onClear = () => {
    onChange();
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Flag}
        placeholder={t(
          'dialogs.task-detail.meta.deadline-deadline.placeholder',
        )}
        label={due}
        end={
          value ? (
            <Styled.TimeLeftContainer>
              <TimeLeft date={value} isMidShort />
            </Styled.TimeLeftContainer>
          ) : undefined
        }
        onClick={openMenu}
        onClear={due ? onClear : undefined}
        selected={menuOpen}
      />

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={buttonRef}
          value={value}
          onChange={_onChange}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          datePresets={datePresets}
          icon={IconOptions.Calendar}
          onOutside={closeMenu}
          removeLabel={t('task.deadline-deadline.options.clear.label')}
          canRemoveValue
        />
      )}
    </>
  );
};
